import { useState, useCallback } from 'react';
import { useNotificationProvider } from 'hooks';
import { CANtrackComment } from 'containers/pages/comments/types/comment';
import { convertToCANtrackComment } from 'containers/pages/comments/utils/convertToCANtrackComment';
import getCommentsByCaseId from 'api/cases/getCommentsByCaseId';
import getActivities from 'api/comments/getActivities';
import updateCase from 'api/cases/updateCase';
import { updateComment } from 'api/comments/updateComment';
import { buildActivityMappings, ActivityMapping } from 'containers/pages/cases/utils/matchActivities';

interface GPDTarget {
	groupId: number;
	groupTitle: string;
	projectId: number;
	projectTitle: string;
	deliverableId: number;
	deliverableTitle: string;
}

interface NewActivity {
	id: number;
	title: string;
}

export interface GPDAReassignState {
	open: boolean;
	loading: boolean;
	applying: boolean;
	caseId: number | null;
	caseTitle: string;
	target: GPDTarget | null;
	casePayload: Record<string, unknown> | null;
	comments: CANtrackComment[];
	newActivities: NewActivity[];
	mappings: ActivityMapping[];
	progress: { done: number; total: number };
}

const initialState: GPDAReassignState = {
	open: false,
	loading: false,
	applying: false,
	caseId: null,
	caseTitle: '',
	target: null,
	casePayload: null,
	comments: [],
	newActivities: [],
	mappings: [],
	progress: { done: 0, total: 0 },
};

function useGPDAReassign() {
	const [state, setState] = useState<GPDAReassignState>(initialState);
	const { notifySuccess, notifyError } = useNotificationProvider();

	/**
	 * Open the reassignment dialog: fetch comments, new activities, build mappings
	 */
	const startReassign = useCallback(async (
		caseId: number,
		caseTitle: string,
		target: GPDTarget,
		casePayload: Record<string, unknown>,
	) => {
		setState((prev) => ({ ...prev, open: true, loading: true, caseId, caseTitle, target, casePayload }));

		try {
			// Fetch comments for this case and activities for the new deliverable in parallel
			const [rawComments, rawActivities] = await Promise.all([
				getCommentsByCaseId(caseId),
				getActivities(target.deliverableId),
			]);

			const comments = rawComments.map(convertToCANtrackComment);
			const newActivities: NewActivity[] = rawActivities.map((a: any) => ({
				id: Number(a.Id),
				title: a.Title,
			}));

			if (comments.length === 0) {
				// No comments to reassign — save the case directly
				await updateCase(casePayload);
				setState(initialState);
				notifySuccess('Case GPD updated. No comments to reassign.');
				return;
			}

			// Collect unique old activities and their comment counts
			const activityMap = new Map<number, { id: number; title: string }>();
			const countMap = new Map<number, number>();
			for (const c of comments) {
				const actId = c.gpda.activity.id;
				if (!activityMap.has(actId)) {
					activityMap.set(actId, { id: actId, title: c.gpda.activity.title });
				}
				countMap.set(actId, (countMap.get(actId) ?? 0) + 1);
			}

			const oldActivities = Array.from(activityMap.values());

			// Build mappings with AI-assisted matching
			const mappings = await buildActivityMappings(oldActivities, countMap, newActivities);

			setState((prev) => ({
				...prev,
				loading: false,
				comments,
				newActivities,
				mappings,
			}));
		} catch (error) {
			console.error('[useGPDAReassign] Failed to prepare reassignment:', error);
			notifyError('Failed to load comments for reassignment');
			setState(initialState);
		}
	}, [notifyError, notifySuccess]);

	/**
	 * Update a single mapping's target activity
	 */
	const updateMapping = useCallback((oldActivityId: number, newActivityId: number) => {
		setState((prev) => ({
			...prev,
			mappings: prev.mappings.map((m) =>
				m.oldActivity.id === oldActivityId
					? { ...m, newActivityId: newActivityId, confidence: 'manual' as const }
					: m,
			),
		}));
	}, []);

	/**
	 * Apply the mappings: update all comments with the new GPDA
	 */
	const applyMappings = useCallback(async () => {
		const { comments, mappings, target, casePayload } = state;
		if (!target) return;

		// Validate all mappings are set
		const unmapped = mappings.filter((m) => m.newActivityId === null);
		if (unmapped.length > 0) {
			notifyError(`${unmapped.length} activity mapping(s) still need to be set`);
			return;
		}

		// Build lookup: old activity ID → new activity ID
		const activityLookup = new Map<number, number>();
		for (const m of mappings) {
			activityLookup.set(m.oldActivity.id, m.newActivityId!);
		}

		// Find the new activity titles for the lookup
		const newActivityLookup = new Map<number, string>();
		for (const m of mappings) {
			const newAct = state.newActivities.find((a) => a.id === m.newActivityId);
			if (newAct) newActivityLookup.set(m.newActivityId!, newAct.title);
		}

		setState((prev) => ({ ...prev, applying: true, progress: { done: 0, total: comments.length } }));

		// Save the case first (with new GPD), then update comments
		if (casePayload) {
			try {
				await updateCase(casePayload);
			} catch (error) {
				console.error('[useGPDAReassign] Failed to save case:', error);
				notifyError('Failed to save case. Comment reassignment aborted.');
				setState(initialState);
				return;
			}
		}

		let successCount = 0;
		let failCount = 0;

		// Update comments sequentially to avoid overwhelming the API
		for (let i = 0; i < comments.length; i++) {
			const comment = comments[i];
			const newActivityId = activityLookup.get(comment.gpda.activity.id);
			if (newActivityId === undefined) continue;

			const updatedComment: CANtrackComment = {
				...comment,
				gpda: {
					group: { id: target.groupId, title: target.groupTitle },
					project: { id: target.projectId, title: target.projectTitle },
					deliverable: { id: target.deliverableId, title: target.deliverableTitle },
					activity: {
						id: newActivityId,
						title: newActivityLookup.get(newActivityId) ?? '',
					},
				},
			};

			try {
				await updateComment(updatedComment);
				successCount++;
			} catch {
				failCount++;
			}

			setState((prev) => ({ ...prev, progress: { done: i + 1, total: comments.length } }));
		}

		if (failCount === 0) {
			notifySuccess(`Updated ${successCount} comment(s) successfully`);
		} else {
			notifyError(`Updated ${successCount}, failed ${failCount} comment(s)`);
		}

		setState(initialState);
	}, [state, notifySuccess, notifyError]);

	const close = useCallback(() => {
		setState(initialState);
	}, []);

	return {
		state,
		startReassign,
		updateMapping,
		applyMappings,
		close,
	};
}

export default useGPDAReassign;
